import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { BarChart, Bar, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  Alert, Button, Card, CardHeader, DatePicker, EmptyState, FilterBar, Metric, ProgressBar, Spinner, Tabs,
} from '@opendate-app/setlist';
import ReportingPageLayout from './ReportingPageLayout';
import DemographicsHeatmap from './DemographicsHeatmap';
import StateTileMap from './StateTileMap';
import ExpandableTableCard from './ExpandableTableCard';
import useReportingPage from '../hooks/useReportingPage';

const COUNT_MODES = [
  { value: 'distinct_fans', label: 'Distinct fans' },
  { value: 'fan_events', label: 'Fan-event pairs' },
  { value: 'tickets', label: 'Tickets' },
];
const COUNT_MODE_HELP = {
  distinct_fans: 'Distinct fans counts each person once, no matter how many events they bought tickets to. Use it to answer “how many people did we reach?”',
  fan_events: 'Fan-event pairs counts each person once per event they bought tickets to, so a fan who came to 5 shows counts 5 times. Use it to see audience across all shows, closer to total attendance.',
  tickets: 'Tickets counts every paid ticket, so a fan who bought 4 tickets counts 4 times. The charts and tables below show tickets sold, not people.',
};
const DATE_MODES = [
  { value: 'purchase_date', label: 'Purchase date' },
  { value: 'event_date', label: 'Event date' },
];
const BAR_COLOR = 'var(--sl-fg-brand, #1982c4)';
const noop = () => {};
const LOADING = <div className="d-flex justify-content-center py-4"><Spinner label="Loading…" /></div>;

const pad = (n) => String(n).padStart(2, '0');
const toIsoDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const formatCount = (n) => Number(n || 0).toLocaleString();

export function buildQuery({ range, countMode, dateMode, filters }) {
  const qs = new URLSearchParams();
  if (range?.start && range?.end) {
    qs.set('start_date', toIsoDate(range.start));
    qs.set('end_date', toIsoDate(range.end));
  }
  qs.set('count_mode', countMode);
  qs.set('date_mode', dateMode);
  Object.entries(filters).forEach(([key, values]) => values.forEach((v) => qs.append(`${key}[]`, v)));
  return qs.toString();
}

export function priorChange(total, prior) {
  if (!prior) return undefined;
  const pct = ((total - prior) / prior) * 100;
  let direction = 'flat';
  if (pct > 0) direction = 'up';
  if (pct < 0) direction = 'down';
  return { value: `${Math.abs(pct).toFixed(1)}%`, direction, label: 'vs prior period' };
}

const shareOf = (count, total) => (total ? `${((count * 100) / total).toFixed(1)}%` : '0%');
const fansColumns = (total, countHeader) => [
  { key: 'count', header: countHeader, align: 'right', render: (row) => formatCount(row.count) },
  { key: 'share', header: 'Share', align: 'right', render: (row) => shareOf(row.count, total) },
];
const cityColumns = (total, countHeader) => [
  { key: 'city', header: 'City', primary: true },
  { key: 'state', header: 'State', render: (row) => row.state || '—' },
  ...fansColumns(total, countHeader),
];
const zipColumns = (total, countHeader) => [
  { key: 'zip', header: 'Zip', primary: true },
  { key: 'city', header: 'City', render: (row) => row.city || '—' },
  { key: 'state', header: 'State', render: (row) => row.state || '—' },
  ...fansColumns(total, countHeader),
];
const stateColumns = (total, countHeader) => [
  { key: 'label', header: 'State', primary: true },
  ...fansColumns(total, countHeader),
];

function AgeTooltip({ active, payload, label, countHeader }) {
  if (!active || !payload?.length) return null;
  const { count, pct } = payload[0].payload;
  return (
    <div
      style={{
        background: 'var(--sl-bg-primary, #fff)',
        border: '1px solid var(--sl-border-secondary, #e4e7ec)',
        borderRadius: 8,
        padding: '8px 12px',
        fontSize: 13,
        color: 'var(--sl-text-primary, #101828)',
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div>{countHeader}: {formatCount(count)}</div>
      <div style={{ color: 'var(--sl-text-secondary, #414651)' }}>% of {countHeader.toLowerCase()}: {pct}%</div>
    </div>
  );
}

function AgeChart({ buckets, countHeader }) {
  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={buckets} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke="var(--sl-border-secondary, #e4e7ec)" />
        <XAxis dataKey="label" tickLine={false} axisLine={false} />
        <YAxis tickLine={false} axisLine={false} tickFormatter={formatCount} />
        <Tooltip cursor={{ fill: 'var(--sl-bg-primary-hover, rgba(152, 162, 179, 0.15))' }} content={<AgeTooltip countHeader={countHeader} />} />
        <Bar dataKey="count" name={countHeader} fill={BAR_COLOR} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function GenderBreakdown({ rows }) {
  return (
    <>
      {rows.map((row) => (
        <div key={row.label} className="mb-3">
          <div
            className="d-flex justify-content-between mb-1"
            style={{ fontSize: 14, color: 'var(--sl-text-primary, #101828)' }}
          >
            <span>{row.label}</span>
            <span>{formatCount(row.count)} · {row.pct}%</span>
          </div>
          <ProgressBar value={row.pct} />
        </div>
      ))}
    </>
  );
}

function ReportingMarketingDemographicsPage({ team, venueOwnerships = [] }) {
  const { isMobile, sidebarOpen, setSidebarOpen } = useReportingPage();
  const [range, setRange] = useState(() => {
    const now = new Date();
    return { start: new Date(now.getFullYear(), 0, 1), end: now };
  });
  const [countMode, setCountMode] = useState('distinct_fans');
  const [dateMode, setDateMode] = useState('purchase_date');
  const [filters, setFilters] = useState({});
  const [options, setOptions] = useState({ events: [], tags: [] });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const basePath = `/teams/${team?.id}/reporting/marketing/demographics`;
  const query = buildQuery({ range, countMode, dateMode, filters });

  useEffect(() => {
    axios.get(`${basePath}/filter_options`)
      .then(({ data: opts }) => setOptions(opts))
      .catch((e) => console.error('Failed to load demographics filter options', e));
  }, [basePath]);

  useEffect(() => {
    if (!range?.start || !range?.end) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.get(`${basePath}/data?${query}`)
      .then(({ data: json }) => { if (!cancelled) setData(json); })
      .catch((e) => {
        console.error('Failed to load demographics', e);
        if (!cancelled) {
          setData(null);
          setError('Could not load demographics. Try again.');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [basePath, query]);

  const filterDefs = useMemo(() => [
    { key: 'venue_ownership_id', label: 'Venue', options: venueOwnerships.map((v) => ({ value: String(v.id), label: v.name })) },
    { key: 'calendar_event_id', label: 'Event', options: options.events.map((e) => ({ value: String(e.id), label: e.name })) },
    { key: 'tag_ids', label: 'Tag', options: options.tags.map((t) => ({ value: String(t.id), label: t.name })) },
  ], [venueOwnerships, options]);

  const toolbar = (
    <div className="d-flex flex-wrap align-items-center w-100" style={{ gap: 8 }}>
      <DatePicker mode="range" size="sm" presets value={range} onChange={setRange} />
      <Tabs variant="button-border" size="sm" collapseBelow={0} items={COUNT_MODES} value={countMode} onChange={setCountMode} />
      <Tabs variant="button-border" size="sm" collapseBelow={0} items={DATE_MODES} value={dateMode} onChange={setDateMode} />
      <div className="ml-auto">
        <Button size="sm" disabled={!range?.start || !range?.end} onClick={() => { window.location.href = `${basePath}/export?${query}`; }}>
          Export CSV
        </Button>
      </div>
      <div className="w-100" style={{ fontSize: 13, color: 'var(--sl-text-tertiary, #535862)' }}>
        {COUNT_MODE_HELP[countMode]}
      </div>
      <div className="w-100">
        <FilterBar variant="plain" filters={filterDefs} values={filters} onChange={setFilters} onClearAll={() => setFilters({})} />
      </div>
    </div>
  );

  const countHeader = countMode === 'tickets' ? 'Tickets' : 'Fans';
  const grid = { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 16 };
  const pending = loading || (!data && !error);
  const showEmpty = !pending && data?.total === 0;
  const showCards = !showEmpty && (pending || data);

  return (
    <ReportingPageLayout
      team={team}
      activeKey="marketing_demographics"
      title="Fan Demographics"
      subtitle="Age, gender, and location of your ticket buyers across every event in the range."
      isMobile={isMobile}
      sidebarOpen={sidebarOpen}
      setSidebarOpen={setSidebarOpen}
      showDatePicker={false}
      showFilters={false}
      activeFilters={{}}
      filterGroups={[]}
      filterSidebarOpen={false}
      setFilterSidebarOpen={noop}
      handleFilterChange={noop}
      handleRemoveFilter={noop}
      handleClearAllFilters={noop}
      toolbarExtras={toolbar}
    >
      {error && <div className="mb-3"><Alert color="error" role="alert" title={error} /></div>}

      <div className="mb-3" style={{ maxWidth: 360 }}>
        <Metric
          label={COUNT_MODES.find((mode) => mode.value === countMode).label}
          value={pending ? '—' : formatCount(data?.total)}
          change={pending ? undefined : priorChange(data?.total, data?.prior_total)}
        />
      </div>

      {showEmpty && (
        <Card>
          <EmptyState title={`No ${countHeader.toLowerCase()} in this range`} description="Try a wider date range or fewer filters." />
        </Card>
      )}

      {showCards && (
        <>
          <div style={grid}>
            <Card header={<CardHeader title={`${countHeader} by Age`} />}>
              {pending ? LOADING : <AgeChart buckets={data.age_buckets} countHeader={countHeader} />}
            </Card>
            <Card header={<CardHeader title={`${countHeader} by Gender`} />}>
              {pending ? LOADING : <GenderBreakdown rows={data.gender} />}
            </Card>
          </div>

          <div className="mb-3">
            <Card header={<CardHeader title={`${countHeader} by Location`} supportingText={`Circle size shows ${countHeader.toLowerCase()} per area; geocoded orders only`} />}>
              {pending ? LOADING : <DemographicsHeatmap points={data.heatmap_points} />}
            </Card>
          </div>

          <div style={{ ...grid, alignItems: 'start' }}>
            <div className="d-flex flex-column" style={{ gap: 16 }}>
              {pending ? (
                <Card header={<CardHeader title="Top Cities" />}>{LOADING}</Card>
              ) : (
                <ExpandableTableCard
                  title="Top Cities"
                  columns={cityColumns(data.total, countHeader)}
                  rows={data.top_cities}
                  getRowKey={(row) => `${row.city}|${row.state}`}
                />
              )}
              {pending ? (
                <Card header={<CardHeader title="Top Zip Codes" />}>{LOADING}</Card>
              ) : (
                <ExpandableTableCard
                  title="Top Zip Codes"
                  columns={zipColumns(data.total, countHeader)}
                  rows={data.top_zips}
                  getRowKey={(row) => row.zip}
                />
              )}
            </div>
            <div className="d-flex flex-column" style={{ gap: 16 }}>
              <Card padding="none" header={<CardHeader title={`${countHeader} by State`} supportingText="Hover a state for its count" />}>
                {pending ? LOADING : <StateTileMap states={data.states} total={data.total} countHeader={countHeader} />}
              </Card>
              {!pending && (
                <ExpandableTableCard
                  title="States"
                  columns={stateColumns(data.total, countHeader)}
                  rows={data.states}
                  getRowKey={(row) => row.label}
                />
              )}
            </div>
          </div>
        </>
      )}
    </ReportingPageLayout>
  );
}

export default ReportingMarketingDemographicsPage;
